import { GPSSource } from "PosterEditor/model/poster-project";
import { useGpsStoreLocal } from "./use-gps-store-local";
import { useGpsStoreRemote } from "./use-gps-store-remote";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useAuthContext } from "BEDROCK/auth/auth-provider";

export const useGpsStore = () => {
  const { isUserAuthenticated } = useAuthContext();
  const {
    saveCoordinates: saveCoordinatesLocal,
    fetchCoordinates: fetchCoordinatesLocal,
    deleteCoordinates: deleteCoordinatesLocal,
  } = useGpsStoreLocal();

  const {
    saveCoordinates: saveCoordinatesRemote,
    fetchCoordinates: fetchCoordinatesRemote,
    copyGPSSourceFromPublicToUser: copyGPSSourceFromPublicToUserRemote,
    deleteCoordinates: deleteCoordinatesRemote,
    copyPublicGPSSource: copyPublicGPSSourceRemote,
    copyUserGPSSource: copyUserGPSSourceRemote,
  } = useGpsStoreRemote();

  const queryClient = useQueryClient();
  const saveCooldownRef = useRef<NodeJS.Timeout | null>(null);

  const saveCoordinates = async (
    coordinates: GPSSource,
    id: string,
    isPublic: boolean,
    forceImmediateRemoteStore?: boolean
  ) => {
    await saveCoordinatesLocal(coordinates, id);
    // Update cache
    queryClient.setQueryData(["gps-coordinates", id], coordinates);

    if (!isUserAuthenticated) {
      return;
    }
    if (forceImmediateRemoteStore) {
      await saveCoordinatesRemote(coordinates, id, isPublic);
    } else {
      if (saveCooldownRef.current) {
        clearTimeout(saveCooldownRef.current);
      }
      saveCooldownRef.current = setTimeout(() => {
        saveCoordinatesRemote(coordinates, id, isPublic);
        saveCooldownRef.current = null;
      }, 10000);
    }
  };

  const loadCoordinates = async (
    id: string,
    isPublic: boolean
  ): Promise<GPSSource> => {
    return await queryClient.fetchQuery({
      queryKey: ["gps-coordinates", id],
      queryFn: async () => {
        // if (!isUserAuthenticated) {
        const localData = await fetchCoordinatesLocal(id);
        if (localData?.allCoordinates !== undefined) {
          return localData;
        }
        // } else {
        const remoteData = await fetchCoordinatesRemote(id, isPublic);
        if (remoteData) {
          await saveCoordinatesLocal(remoteData, id);
        }
        return remoteData;
        // }
      },
      staleTime: 1000 * 60 * 5,
    });
  };

  const copyGPSSourceFromPublicToUser = async (id: string) => {
    const publicData = await fetchCoordinatesRemote(id, true);
    if (publicData) {
      await saveCoordinatesLocal(publicData, id);
      await copyGPSSourceFromPublicToUserRemote(id);
      queryClient.invalidateQueries({ queryKey: ["gps-coordinates", id] });
    }
  };

  const copyPublicGPSSource = async (id: string) => {
    const newID = await copyPublicGPSSourceRemote(id);
    queryClient.invalidateQueries({ queryKey: ["gps-coordinates", id] });
    return newID;
  };

  const copyUserGPSSource = async (id: string) => {
    const newID = await copyUserGPSSourceRemote(id);
    queryClient.invalidateQueries({ queryKey: ["gps-coordinates", id] });
    return newID;
  };

  const deleteCoordinates = async (id: string, isPublic: boolean) => {
    await deleteCoordinatesLocal(id);
    if (!isUserAuthenticated) {
      return;
    }
    await deleteCoordinatesRemote([id], isPublic);
    // Invalidate cache
    queryClient.invalidateQueries({ queryKey: ["gps-coordinates", id] });
  };

  return {
    saveCoordinates,
    loadCoordinates,
    deleteCoordinates,
    copyGPSSourceFromPublicToUser,
    copyPublicGPSSource,
    copyUserGPSSource,
  };
};
